/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : selectFileSlice.ts
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Redux Slice for  Select APM File state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Jan 05, 2022
 *
 * @Version      : Version 1.0
 *
 * By            : H422337
 * On            : Jan 05, 2022
 * Description   : Create a customer store
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ISelectApmFile,
  ISelectApmFilesList,
  ISelectApmFileState,
} from "models/selectApmFile";
import { AppThunk, RootState } from "stores/GlobalStore";
import { setSelectedApmRecordData } from "stores/ApmWorkFlowStore";
import { apiCallBegan, apiVersion, apmURLs } from "api";

export const initialState: ISelectApmFileState = {
  apmFilesList: null,
  selectedApmFile: null,
  apmFilesLoader: false,
  showPostScriptOnly: true,
};

const selectApmFileSlice = createSlice({
  name: "selectApmFile",
  initialState,
  reducers: {
    startApmFilesLoader(selectApmFileState: ISelectApmFileState) {
      selectApmFileState.apmFilesLoader = true;
    },
    setSelectApmFileList(
      selectApmFileState: ISelectApmFileState,
      action: PayloadAction<ISelectApmFilesList>
    ) {
      selectApmFileState.apmFilesList = action.payload.partNumbers;
      selectApmFileState.apmFilesLoader = false;
    },
    resetSelectApmFilesList(selectApmFileState: ISelectApmFileState) {
      selectApmFileState.apmFilesList = null;
      selectApmFileState.apmFilesLoader = false;
    },
    setSelectedApmFile(
      selectApmFileState: ISelectApmFileState,
      action: PayloadAction<ISelectApmFile | null>
    ) {
      selectApmFileState.selectedApmFile = action.payload;
    },
    setSelectedPostScriptValue(
      selectApmFileState: ISelectApmFileState,
      action: PayloadAction<boolean>
    ) {
      selectApmFileState.showPostScriptOnly = action.payload;
    },
    resetSelectedApmFile(selectApmFileState: ISelectApmFileState) {
      selectApmFileState.selectedApmFile = initialState.selectedApmFile;
      selectApmFileState.showPostScriptOnly = initialState.showPostScriptOnly;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setSelectedApmRecordData,
      (selectApmFileState: ISelectApmFileState, action) => {
        if (
          action.payload.baseFileName !== "" &&
          action.payload.baseFileName &&
          action.payload.lsapFileName !== "" &&
          action.payload.lsapFileName
        ) {
          selectApmFileState.selectedApmFile = {
            partNumber: action.payload.baseFileName,
            lsapFileName: action.payload.lsapFileName,
          };
        } else {
          selectApmFileState.selectedApmFile = null;
        }
      }
    );
  },
});

export const {
  startApmFilesLoader,
  setSelectApmFileList,
  resetSelectApmFilesList,
  setSelectedApmFile,
  setSelectedPostScriptValue,
  resetSelectedApmFile,
} = selectApmFileSlice.actions;

export const selectApmFileReducer = selectApmFileSlice.reducer;

export const fetchAPMFile = (): AppThunk => async (dispatch, getState) => {
  const customerId = getState().customer.selectedCustomer?.customerId;
  const url = apiVersion.v1 + apmURLs.APM_SETTINGS_FILE_LIST_URL;
  const params = { customerId: customerId };
  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: startApmFilesLoader.type,
      onSuccess: setSelectApmFileList.type,
      onError: resetSelectApmFilesList.type,
      showGlobalError: true,
    })
  );
};

export const selectApmFileNames = (state: RootState): ISelectApmFile[] | null =>
  state.selectApmFile.apmFilesList;
export const selectApmFilesLoader = (state: RootState): boolean =>
  state.selectApmFile.apmFilesLoader;
export const selectSelectedFileName = (
  state: RootState
): ISelectApmFile | null => state.selectApmFile.selectedApmFile;

export const selectshowPostScriptOnly = (state: RootState): boolean =>
  state.selectApmFile.showPostScriptOnly;
